import React from 'react';
import { Form } from 'react-bootstrap';
import { normalizeExpiryDate, formatExpiryDateDisplay, getExpiryStatus } from '../../helpers/dateUtils';

/**
 * Converts whatever shape the API gave us into the YYYY-MM a native month input needs.
 */
function toMonthInputValue(dateStr) {
  const value = dateStr || '';
  if (/^\d{4}-\d{2}$/.test(value)) {
    return value;
  }
  if (/^\d{2}\/\d{4}$/.test(value)) {
    const [month, year] = value.split('/');
    return `${year}-${month}`;
  }
  const normalized = normalizeExpiryDate(value);
  if (normalized && /^\d{2}\/\d{4}$/.test(normalized)) {
    const [month, year] = normalized.split('/');
    return `${year}-${month}`;
  }
  return '';
}

/**
 * One editable row of the product approval table.
 *
 * This is a PureComponent on purpose. The parent keeps every product in state and
 * rebuilds the array on each keystroke, so without this boundary a single character
 * typed anywhere re-renders every row on the page. At 500 rows that is roughly 6,000
 * react-bootstrap components per keystroke. Because only the edited product object
 * gets a new identity, the shallow prop compare here means just that one row rerenders.
 *
 * Every prop must therefore stay referentially stable: the callbacks are class fields
 * on the parent (created once), and `isSelected` is a plain boolean rather than a
 * lookup into the selection array.
 *
 * Layout: the table runs `table-layout: fixed` against the colgroup budget declared in
 * ProductApprovalDetail, so column widths come from CSS and never from cell content.
 * Cells must not set widths of their own or they fight that budget. Any value that can
 * outgrow its column carries a `title`, so nothing is ever unreachable.
 */
class ProductApprovalRow extends React.PureComponent {
  render() {
    const {
      product,
      index,
      isSelected,
      isSaving,
      onFieldEdit,
      onSaveProduct,
      onSelect,
      onApprove,
      onReject
    } = this.props;

    const expiryStatus = product.expiry_date ? getExpiryStatus(product.expiry_date) : null;
    const isExpired = !!(expiryStatus && expiryStatus.isExpired);

    return (
      <tr className={`pa-row${isSelected ? ' is-selected' : ''}`}>
        <td className="pa-cell pa-cell-check">
          <Form.Check
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(product.id)}
            aria-label="Select product"
          />
        </td>

        <td className="pa-cell pa-cell-index">{index + 1}</td>

        <td className="pa-cell pa-cell-original">
          <div className="pa-original-name" title={product.original_medicine_name}>
            {product.original_medicine_name}
          </div>
          {product.original_expiry_date && (
            <div className="pa-original-expiry">
              Exp {formatExpiryDateDisplay(product.original_expiry_date)}
            </div>
          )}
        </td>

        <td className="pa-cell">
          <Form.Control
            type="text"
            className="pa-input pa-input-strong"
            value={product.medicinename}
            title={product.medicinename}
            onChange={(e) => onFieldEdit(product.id, 'medicinename', e.target.value)}
            onBlur={() => onSaveProduct(product)}
          />
        </td>

        <td className="pa-cell">
          <Form.Control
            type="text"
            className="pa-input"
            value={product.manufacturer || ''}
            title={product.manufacturer || ''}
            onChange={(e) => onFieldEdit(product.id, 'manufacturer', e.target.value)}
            onBlur={() => onSaveProduct(product)}
          />
        </td>

        <td className="pa-cell">
          <Form.Control
            type="text"
            className="pa-input"
            value={product.size || ''}
            title={product.size || ''}
            onChange={(e) => onFieldEdit(product.id, 'size', e.target.value)}
            onBlur={() => onSaveProduct(product)}
          />
        </td>

        <td className="pa-cell">
          <Form.Control
            type="text"
            className="pa-input"
            value={product.packaging || ''}
            title={product.packaging || ''}
            placeholder="Strip of 10"
            onChange={(e) => onFieldEdit(product.id, 'packaging', e.target.value)}
            onBlur={() => onSaveProduct(product)}
          />
        </td>

        <td className="pa-cell">
          <Form.Control
            as="select"
            className="pa-input pa-select"
            value={product.form || ''}
            title={product.form || ''}
            onChange={(e) => onFieldEdit(product.id, 'form', e.target.value)}
            onBlur={() => onSaveProduct(product)}
          >
            <option value="">Select</option>
            <option value="Tablet">Tablet</option>
            <option value="Capsule">Capsule</option>
            <option value="Syrup">Syrup</option>
            <option value="Injection">Injection</option>
            <option value="Cream">Cream</option>
            <option value="Ointment">Ointment</option>
            <option value="Drops">Drops</option>
          </Form.Control>
        </td>

        <td className="pa-cell">
          <Form.Control
            type="text"
            className="pa-input"
            value={product.batch || ''}
            title={product.batch || ''}
            placeholder="Batch10"
            onChange={(e) => onFieldEdit(product.id, 'batch', e.target.value)}
            onBlur={() => onSaveProduct(product)}
          />
        </td>

        <td className="pa-cell pa-cell-expiry">
          <Form.Control
            type="month"
            className={`pa-input${isExpired ? ' is-expired' : ''}`}
            value={toMonthInputValue(product.expiry_date)}
            title={formatExpiryDateDisplay(product.expiry_date)}
            onChange={(e) => onFieldEdit(product.id, 'expiry_date', e.target.value)}
            onBlur={() => onSaveProduct(product)}
          />
          {/* The strip is always rendered, even with no verdict to show. Every
              other column reserves the same height below its input, so this
              cell must keep its own reserve whether or not a badge fills it -
              otherwise a product with no expiry date drops its input off the
              line the rest of the row sits on. */}
          <div className="pa-expiry-strip">
            {expiryStatus && (
              <span className={`pa-expiry-note pa-expiry-${expiryStatus.color}`}>
                {expiryStatus.label}
              </span>
            )}
          </div>
        </td>

        <td className="pa-cell">
          <Form.Control
            type="number"
            className="pa-input pa-input-num"
            value={product.quantity || '0'}
            min="0"
            placeholder="100"
            onChange={(e) => onFieldEdit(product.id, 'quantity', e.target.value)}
            onBlur={() => onSaveProduct(product)}
          />
        </td>

        <td className="pa-cell">
          <Form.Control
            type="number"
            step="0.01"
            className="pa-input pa-input-num"
            value={product.mrp || '0'}
            min="0"
            placeholder="150.00"
            onChange={(e) => onFieldEdit(product.id, 'mrp', e.target.value)}
            onBlur={() => onSaveProduct(product)}
          />
        </td>

        <td className="pa-cell pa-cell-actions">
          <div className="pa-actions">
            <button
              type="button"
              className="pa-action pa-action-approve"
              onClick={() => onApprove(product)}
              disabled={isSaving}
              title="Approve"
              aria-label="Approve"
            >
              <i className="fa fa-check"></i>
            </button>
            <button
              type="button"
              className="pa-action pa-action-reject"
              onClick={() => onReject(product)}
              disabled={isSaving}
              title="Reject"
              aria-label="Reject"
            >
              <i className="fa fa-times"></i>
            </button>
          </div>
        </td>
      </tr>
    );
  }
}

export default ProductApprovalRow;
